import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Popup } from 'devextreme-react/popup';
import { ToolbarItem } from 'devextreme-react/popup';
import { Form, SimpleItem, GroupItem, Label, Item as FormItem } from 'devextreme-react/form';
import { DataGrid, Column, Selection, SearchPanel, Paging, Pager, FilterRow, Editing, Button as GridButton, Toolbar as GridToolbar, Item as GridToolbarItem } from 'devextreme-react/data-grid';
import { Button, LoadIndicator, SelectBox, TextBox, DateBox, TextArea } from 'devextreme-react';
import { newToast } from '../../../components';
import { useScreenSize } from '../../../utils/media-query';
import { useAppSelector } from '../../../contexts/hooks';
import { RemisionFormData, EstudioSeleccionado, OrdenEstudioRemision } from '../../../types/types.remissions';
import RemisionesAPI from '../../../api/api.remmisions';

interface ModalCreateRemisionProps {
  visible: boolean;
  wrapperAttr?: { class: string };
  onClose: (refresh?: boolean) => void;
}

export const ModalCreateRemision: React.FC<ModalCreateRemisionProps> = ({ visible, onClose, wrapperAttr = { class: '' } }) => {
  // Estados principales
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [ordenesDisponibles, setOrdenesDisponibles] = useState<OrdenEstudioRemision[]>([]);
  const [estudiosSeleccionados, setEstudiosSeleccionados] = useState<EstudioSeleccionado[]>([]);
  const [selectedRows, setSelectedRows] = useState<OrdenEstudioRemision[]>([]);
  const { getOrdenesPendientesRemision } = RemisionesAPI();

  // Datos del formulario
  const [formData, setFormData] = useState<RemisionFormData>({
    laboratorioId: null,
    fechaInicio: null,
    fechaFin: null,
    temperaturaNevera: '',
    temperaturaAmbiente: '',
    notas: '',
    responsableEnvio: '',
  });

  // Referencias
  const formRef = useRef<Form>(null);
  const gridOrdenesRef = useRef<DataGrid>(null);
  const gridSeleccionadosRef = useRef<DataGrid>(null);

  // Screen size
  const { isSmall, isXSmall } = useScreenSize();

  // API
  const api = RemisionesAPI();

  // Obtener laboratorios desde Redux
  const laboratorios = useAppSelector((state) => state.configuration.Data?.ComboBox?.find((combo) => combo.Name === 'SysA_LaboratoriosRemisiones')?.Data || []);

  // Filtrar estudios disponibles - excluir los que ya están seleccionados
  const estudiosDisponiblesFiltrados = useMemo(() => {
    return ordenesDisponibles.filter((orden) => !estudiosSeleccionados.some((e) => e.Roe_Id === orden.Roe_Id));
  }, [ordenesDisponibles, estudiosSeleccionados]);

  // Cargar datos iniciales
  useEffect(() => {
    if (visible) {
      resetForm();
    }
  }, [visible]);

  // // Cargar órdenes cuando se selecciona laboratorio
  // useEffect(() => {
  //   if (formData.laboratorioId && visible) {
  //     loadOrdenesPendientes();
  //   }
  // }, [formData.laboratorioId, visible]);

  // useEffect(() => {
  //   if (formData.laboratorioId && formData.fechaInicio && formData.fechaFin && visible) {
  //     loadOrdenesPendientes();
  //   }
  // }, [formData.laboratorioId, formData.fechaInicio, formData.fechaFin, visible]);

  const resetForm = useCallback(() => {
    const hoy = new Date();
    const inicioDia = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate());
    setCurrentStep(1);
    setFormData({
      laboratorioId: null,
      fechaInicio: inicioDia,
      fechaFin: inicioDia,
      temperaturaNevera: '',
      temperaturaAmbiente: '',
      notas: '',
      responsableEnvio: '',
    });
    setEstudiosSeleccionados([]);
    setOrdenesDisponibles([]);
  }, []);

  const loadOrdenesPendientes = async () => {
    if (!formData.laboratorioId) return;

    try {
      setLoading(true);

      const ordenes = await getOrdenesPendientesRemision(formData.laboratorioId, formData.fechaInicio, formData.fechaFin);

      if (ordenes && ordenes.length > 0) {
        setOrdenesDisponibles(ordenes);
      } else {
        setOrdenesDisponibles([]);
      }
    } catch (error) {
      console.error('Error cargando órdenes pendientes:', error);
      newToast('ERROR', 'Error al cargar órdenes pendientes', 4);
      setOrdenesDisponibles([]);
    } finally {
      setLoading(false);
    }
  };

  const handleNext = useCallback(async () => {
    if (currentStep === 1) {
      // Validaciones del paso 1
      if (!formData.laboratorioId) {
        newToast('WARNING', 'Debe seleccionar un laboratorio', 4);
        return;
      }
      if (!formData.fechaInicio) {
        newToast('WARNING', 'Debe seleccionar la fecha de inicio', 4);
        return;
      }
      if (!formData.fechaFin) {
        newToast('WARNING', 'Debe seleccionar la fecha de fin', 4);
        return;
      }
      if (formData.fechaInicio > formData.fechaFin) {
        newToast('WARNING', 'La fecha de inicio no puede ser mayor a la fecha de fin', 4);
        return;
      }

      try {
        setLoading(true);
        await loadOrdenesPendientes();

        // Solo avanzar al siguiente paso si la carga fue exitosa
        setCurrentStep(2);
      } catch (error) {
        console.error('Error al cargar órdenes:', error);
        // No avanzar si hay error
      } finally {
        setLoading(false);
      }

      return; // ✅ IMPORTANTE: Salir aquí, no ejecutar el setCurrentStep del final
    }

    if (currentStep === 2) {
      if (estudiosSeleccionados.length === 0) {
        newToast('WARNING', 'Debe seleccionar al menos un estudio', 4);
        return;
      }
    }

    // Para otros pasos (paso 2 -> 3), avanzar normalmente
    setCurrentStep((prev) => prev + 1);
  }, [currentStep, formData.laboratorioId, formData.fechaInicio, formData.fechaFin, estudiosSeleccionados.length, loadOrdenesPendientes]);

  const handlePrevious = useCallback(() => {
    setCurrentStep((prev) => prev - 1);
  }, []);

  // Manejo de estudios
  const handleSelectEstudio = useCallback(
    (orden: OrdenEstudioRemision) => {
      const yaSeleccionado = estudiosSeleccionados.find((e) => e.Roe_Id === orden.Roe_Id);
      if (yaSeleccionado) {
        newToast('WARNING', 'Este estudio ya está seleccionado', 3);
        return;
      }

      const nuevoEstudio: EstudioSeleccionado = {
        ...orden,
        Rmd_Cantidad: 1,
        Rmd_Urgente: false,
        Rmd_Nota: '',
      };

      setEstudiosSeleccionados((prev) => [...prev, nuevoEstudio]);
      newToast('SUCCESS', `Estudio ${orden.Es_Codigo} agregado`, 2);
    },
    [estudiosSeleccionados]
  );

  const handleRemoveEstudio = useCallback((roeId: number) => {
    setEstudiosSeleccionados((prev) => prev.filter((e) => e.Roe_Id !== roeId));
    newToast('INFO', 'Estudio removido', 2);
  }, []);

  // Crear remisión
  const handleCreate = async () => {
    if (estudiosSeleccionados.length === 0) {
      newToast('WARNING', 'Debe seleccionar al menos un estudio', 4);
      return;
    }

    setLoading(true);
    try {
      // Preparar datos de la remisión
      const remisionData = {
        Rm_Lr_Id: formData.laboratorioId,
        Rm_TemperaturaNevera: formData.temperaturaNevera || null,
        Rm_TemperaturaAmbiente: formData.temperaturaAmbiente || null,
        Rm_Notas: formData.notas || null,
        Rm_ResponsableEnvio: formData.responsableEnvio || null,
        DetalleRemisiones: estudiosSeleccionados.map((estudio) => ({
          Rmd_Es_Id: estudio.Es_Id,
          Rmd_Ord_Id: estudio.Ord_Id,
          Rmd_Cantidad: estudio.Rmd_Cantidad,
          Rmd_Urgente: estudio.Rmd_Urgente,
          Rmd_Nota: estudio.Rmd_Nota || null,
        })),
      };
      // Llamar al API real
      const result = await api.createRemision(remisionData, setLoading, (data) => {
        onClose(true); // Refresh = true
      });
    } catch (error) {
      newToast('ERROR', 'Error al crear la remisión', 4);
      setLoading(false);
    }
  };

  // Funciones para el paso 2
  // Función para manejar selección múltiple
  const handleSelectionChanged = useCallback((e: any) => {
    setSelectedRows(e.selectedRowsData || []);
  }, []);

  // Función para agregar estudios seleccionados
  const handleAgregarSeleccionados = useCallback(() => {
    if (selectedRows.length === 0) {
      newToast('WARNING', 'Debe seleccionar al menos un estudio', 3);
      return;
    }

    let agregados = 0;
    let yaExistentes = 0;

    selectedRows.forEach((orden) => {
      const yaSeleccionado = estudiosSeleccionados.find((e) => e.Roe_Id === orden.Roe_Id);
      if (!yaSeleccionado) {
        const nuevoEstudio: EstudioSeleccionado = {
          ...orden,
          Rmd_Cantidad: 1,
          Rmd_Urgente: false,
          Rmd_Nota: '',
        };
        setEstudiosSeleccionados((prev) => [...prev, nuevoEstudio]);
        agregados++;
      } else {
        yaExistentes++;
      }
    });

    // Limpiar selección
    setSelectedRows([]);
    gridOrdenesRef.current?.instance?.clearSelection();

    // Mostrar mensajes informativos
    if (agregados > 0) {
      newToast('SUCCESS', `${agregados} estudio(s) agregado(s) exitosamente`, 3);
    }
    if (yaExistentes > 0) {
      newToast('INFO', `${yaExistentes} estudio(s) ya estaba(n) seleccionado(s)`, 3);
    }
  }, [selectedRows, estudiosSeleccionados]);

  // Función para seleccionar todos los estudios disponibles
  const handleSeleccionarTodos = useCallback(() => {
    if (estudiosDisponiblesFiltrados.length === 0) {
      newToast('INFO', 'Todos los estudios ya están seleccionados', 3);
      return;
    }

    const nuevosEstudios: EstudioSeleccionado[] = estudiosDisponiblesFiltrados.map((orden) => ({
      ...orden,
      Rmd_Cantidad: 1,
      Rmd_Urgente: false,
      Rmd_Nota: '',
    }));

    setEstudiosSeleccionados((prev) => [...prev, ...nuevosEstudios]);
    newToast('SUCCESS', `${nuevosEstudios.length} estudio(s) agregado(s) automáticamente`, 3);
  }, [estudiosDisponiblesFiltrados]);

  // Función para limpiar todas las selecciones
  const handleLimpiarSelecciones = useCallback(() => {
    setEstudiosSeleccionados([]);
    setSelectedRows([]);
    gridOrdenesRef.current?.instance?.clearSelection();
    newToast('INFO', 'Todas las selecciones han sido eliminadas', 2);
  }, []);

  // Renderizado de pasos
  const renderStep1 = () => (
    <div className='step-content'>
      <div className='form-container'>
        <Form ref={formRef}>
          {/* PRIMERA FILA: Laboratorio y Responsable */}
          <GroupItem colCount={isSmall ? 1 : 2}>
            <FormItem>
              <SelectBox label='Laboratorio de Remisión *' stylingMode='outlined' labelMode='static' dataSource={laboratorios} displayExpr='Lr_Nombre' valueExpr='Lr_Id' value={formData.laboratorioId} onValueChanged={(e) => setFormData((prev) => ({ ...prev, laboratorioId: e.value }))} searchEnabled={true} />
            </FormItem>

            <FormItem>
              <TextBox label='Responsable de Envío' stylingMode='outlined' labelMode='static' value={formData.responsableEnvio} onValueChanged={(e) => setFormData((prev) => ({ ...prev, responsableEnvio: e.value }))} />
            </FormItem>
          </GroupItem>

          {/* SEGUNDA FILA: Fechas de búsqueda */}
          <GroupItem colCount={isSmall ? 1 : 2}>
            <FormItem>
              <DateBox
                label='Fecha Inicio de Búsqueda *'
                stylingMode='outlined'
                labelMode='static'
                value={formData.fechaInicio || undefined}
                onValueChanged={(e) => {
                  const newValue = e.value;
                  if (newValue && formData.fechaFin && newValue > formData.fechaFin) {
                    setFormData((prev) => ({ ...prev, fechaInicio: newValue, fechaFin: newValue }));
                  } else {
                    setFormData((prev) => ({ ...prev, fechaInicio: newValue }));
                  }
                }}
                displayFormat='dd/MM/yyyy'
                max={formData.fechaFin || undefined}
                showClearButton={false}
              />
            </FormItem>

            <FormItem>
              <DateBox label='Fecha Fin de Búsqueda *' stylingMode='outlined' labelMode='static' value={formData.fechaFin || undefined} onValueChanged={(e) => setFormData((prev) => ({ ...prev, fechaFin: e.value }))} displayFormat='dd/MM/yyyy' min={formData.fechaInicio || undefined} max={new Date()} showClearButton={false} />
            </FormItem>
          </GroupItem>

          {/* TERCERA FILA: Temperaturas */}
          <GroupItem colCount={isSmall ? 1 : 2}>
            <FormItem>
              <TextBox label='Temperatura Nevera' stylingMode='outlined' labelMode='static' value={formData.temperaturaNevera} onValueChanged={(e) => setFormData((prev) => ({ ...prev, temperaturaNevera: e.value }))} />
            </FormItem>

            <FormItem>
              <TextBox label='Temperatura Ambiente' stylingMode='outlined' labelMode='static' value={formData.temperaturaAmbiente} onValueChanged={(e) => setFormData((prev) => ({ ...prev, temperaturaAmbiente: e.value }))} />
            </FormItem>
          </GroupItem>

          {/* CUARTA FILA: Notas */}
          <GroupItem colCount={1}>
            <FormItem>
              <TextArea label='Notas' stylingMode='outlined' labelMode='static' value={formData.notas} onValueChanged={(e) => setFormData((prev) => ({ ...prev, notas: e.value }))} height={60} />
            </FormItem>
          </GroupItem>
        </Form>
      </div>

    </div>
  );

  const renderStep2 = () => (
    <div className='step-content step-content-estudios'>
      <div className='estudios-container'>
        <div className='ordenes-disponibles'>
          {loading ? (
            <div className='loading-container'>
              <LoadIndicator height={40} width={40} />
              <p>Cargando órdenes pendientes...</p>
            </div>
          ) : estudiosDisponiblesFiltrados.length === 0 && ordenesDisponibles.length === 0 ? (
            <div className='no-data-container'>
              <p>No hay órdenes pendientes de remisión para este laboratorio</p>
            </div>
          ) : estudiosDisponiblesFiltrados.length === 0 && ordenesDisponibles.length > 0 ? (
            <div className='no-data-container'>
              <p>Todos los estudios disponibles han sido agregados</p>
            </div>
          ) : (
            <DataGrid ref={gridOrdenesRef} dataSource={estudiosDisponiblesFiltrados} keyExpr='Roe_Id' height='100%' showBorders={true} rowAlternationEnabled={true} hoverStateEnabled={true} columnAutoWidth={true} width={'auto'} onSelectionChanged={handleSelectionChanged}>
              <SearchPanel visible={true} placeholder='Buscar...' />
              <FilterRow visible={true} />
              <Paging enabled={true} />
              <Pager visible={true} allowedPageSizes={[20, 50, 100, 'all']} displayMode='full' showPageSizeSelector={true} showInfo={true} showNavigationButtons={true} />
              <Selection mode='multiple' />

              <GridToolbar>
                <GridToolbarItem location='before'>
                  <h4 style={{ margin: 0, fontSize: '13px', fontWeight: 600 }}>Órdenes Disponibles ({estudiosDisponiblesFiltrados.length})</h4>
                </GridToolbarItem>
                <GridToolbarItem location='after'>
                  <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                    <Button text='Seleccionar Todos' type='default' stylingMode='contained' icon='check' onClick={handleSeleccionarTodos} disabled={loading || estudiosDisponiblesFiltrados.length === 0} hint='Agregar todos los estudios disponibles' />
                    {selectedRows.length > 0 && <Button text={`Agregar (${selectedRows.length})`} type='default' stylingMode='contained' icon='plus' onClick={handleAgregarSeleccionados} hint='Agregar los estudios seleccionados' />}
                    {estudiosSeleccionados.length > 0 && <Button text='Limpiar Todo' type='danger' stylingMode='contained' icon='clear' onClick={handleLimpiarSelecciones} hint='Eliminar todos los estudios seleccionados' />}
                  </div>
                </GridToolbarItem>
                <GridToolbarItem name='searchPanel' />
              </GridToolbar>

              <Column caption='' alignment='center' width={40} cellRender={({ data }) => <Button icon='add' type='default' stylingMode='text' onClick={() => handleSelectEstudio(data)} hint='Agregar este estudio' />} />
              <Column dataField='Ord_Numero' caption='ORDEN' width={100} />
              <Column dataField='Paciente_Nombre' caption='PACIENTE' width={200} />
              <Column dataField='Paciente_Identificacion' caption='IDENTIFICACIÓN' width={120} />
              <Column dataField='Es_Codigo' caption='CÓDIGO' width={100} />
              <Column dataField='Es_Nombre' caption='ESTUDIO' />
              <Column dataField='Area_Nombre' caption='ÁREA' width={150} />
              <Column dataField='Ord_FechaCreacion' caption='FECHA' dataType='date' format='dd/MM/yyyy' width={100} />
            </DataGrid>
          )}
        </div>

        {estudiosSeleccionados.length > 0 && (
          <div className='estudios-seleccionados'>
            <DataGrid ref={gridSeleccionadosRef} dataSource={estudiosSeleccionados} keyExpr='Roe_Id' height='100%' showBorders={true} rowAlternationEnabled={true} columnAutoWidth={true}>
              <Editing mode='cell' allowUpdating={true} allowDeleting={false} />
              <Paging enabled={true} />
              <Pager visible={true} allowedPageSizes={[20, 50, 100, 'all']} displayMode='full' showPageSizeSelector={true} showInfo={true} showNavigationButtons={true} />

              <GridToolbar>
                <GridToolbarItem location='before'>
                  <h4 style={{ margin: 0, fontSize: '13px', fontWeight: 600 }}>Estudios Seleccionados ({estudiosSeleccionados.length})</h4>
                </GridToolbarItem>
                <GridToolbarItem location='after'>
                  <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                    <Button text='Marcar Urgentes' type='default' stylingMode='contained' icon='lightning' onClick={() => { setEstudiosSeleccionados((prev) => prev.map((e) => ({ ...e, Rmd_Urgente: true }))); newToast('INFO', 'Todos marcados como urgentes', 2); }} hint='Marcar todos como urgentes' />
                    <Button text='Quitar Urgencia' type='normal' stylingMode='contained' icon='remove' onClick={() => { setEstudiosSeleccionados((prev) => prev.map((e) => ({ ...e, Rmd_Urgente: false }))); newToast('INFO', 'Urgencia removida', 2); }} hint='Remover urgencia' />
                  </div>
                </GridToolbarItem>
              </GridToolbar>

              <Column dataField='Ord_Numero' caption='ORDEN' width={100} allowEditing={false} />
              <Column dataField='Paciente_Nombre' caption='PACIENTE' width={180} allowEditing={false} />
              <Column dataField='Es_Codigo' caption='CÓDIGO' width={100} allowEditing={false} />
              <Column dataField='Es_Nombre' caption='ESTUDIO' allowEditing={false} />
              <Column dataField='Rmd_Cantidad' caption='CANT.' dataType='number' alignment='center' width={70} editorOptions={{ min: 1, max: 999 }} />
              <Column dataField='Rmd_Urgente' caption='URG.' dataType='boolean' width={60} />
              <Column dataField='Rmd_Nota' caption='NOTA' minWidth={120} editorOptions={{ placeholder: 'Observaciones...' }} />
              <Column type='buttons' caption='' width={50}>
                <GridButton icon='trash' hint='Eliminar estudio' onClick={(e) => { handleRemoveEstudio(e.row?.data?.Roe_Id); }} />
              </Column>
            </DataGrid>
          </div>
        )}
      </div>
    </div>
  );

  const renderStep3 = () => {
    const laboratorioSeleccionado = laboratorios.find((l) => l.Lr_Id === formData.laboratorioId);

    // Función para renderizar badges de urgente
    const renderUrgente = (cellData: any) => {
      return cellData.value ? (
        <span
          className='urgente-badge'
          style={{
            backgroundColor: '#ff4444',
            color: 'white',
            padding: '2px 8px',
            borderRadius: '12px',
            fontSize: '11px',
            fontWeight: 'bold',
          }}>
          URGENTE
        </span>
      ) : (
        <span>NORMAL</span>
      );
    };

    // Función para renderizar cantidad con badge
    const renderCantidad = (cellData: any) => {
      const cantidad = cellData.value || 1;
      return (
        <div style={{ textAlign: 'center' }}>
          {cantidad > 1 ? (
            <span
              className='cantidad-badge'
              style={{
                backgroundColor: '#007acc',
                color: 'white',
                padding: '2px 8px',
                borderRadius: '12px',
                fontSize: '11px',
                fontWeight: 'bold',
              }}>
              x{cantidad}
            </span>
          ) : (
            <span style={{ color: '#666' }}>1</span>
          )}
        </div>
      );
    };

    // Función para renderizar el código del estudio con highlight
    const renderEstudioCodigo = (cellData: any) => {
      return (
        <div
          style={{
            fontWeight: 'bold',
            color: '#007acc',
            fontSize: '13px',
          }}>
          {cellData.value}
        </div>
      );
    };

    // Función para renderizar información del paciente
    const renderPaciente = (cellData: any) => {
      const data = cellData.data;
      return (
        <div>
          <div style={{ fontWeight: 'bold', fontSize: '13px' }}>{data.Paciente_Nombre}</div>
          <div style={{ fontSize: '11px', color: '#666' }}>ID: {data.Paciente_Identificacion}</div>
        </div>
      );
    };

    // Función para renderizar número de orden
    const renderOrden = (cellData: any) => {
      return (
        <div
          style={{
            fontWeight: 'bold',
            color: '#333',
            textAlign: 'center',
          }}>
          #{cellData.value}
        </div>
      );
    };

    return (
      <div className='step-content' style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div className='confirmation-content' style={{ display: 'flex', flexDirection: 'column', gap: '16px', flex: 1, overflow: 'auto' }}>
          {/* Información General en Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px' }}>
            {/* Card Laboratorio */}
            <div
              className='info-card'
              style={{
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                padding: '16px',
                backgroundColor: '#f8f9fa',
              }}>
              <h4 style={{ margin: '0 0 12px 0', color: '#007acc', fontSize: '16px' }}>Laboratorio</h4>
              <div className='summary-item'>
                <strong style={{ fontSize: '14px' }}>{laboratorioSeleccionado?.Lr_Nombre}</strong>
                {laboratorioSeleccionado?.Lr_Direccion && <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>📍 {laboratorioSeleccionado.Lr_Direccion}</div>}
                {laboratorioSeleccionado?.Lr_Telefono && <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>📞 {laboratorioSeleccionado.Lr_Telefono}</div>}
              </div>
            </div>

            {/* Card Responsable */}
            {formData.responsableEnvio && (
              <div
                className='info-card'
                style={{
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  padding: '16px',
                  backgroundColor: '#f8f9fa',
                }}>
                <h4 style={{ margin: '0 0 12px 0', color: '#007acc', fontSize: '16px' }}>Responsable de Envío</h4>
                <div style={{ fontSize: '14px', fontWeight: '500' }}>{formData.responsableEnvio}</div>
              </div>
            )}

            {/* Card Temperaturas */}
            {(formData.temperaturaNevera || formData.temperaturaAmbiente) && (
              <div
                className='info-card'
                style={{
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  padding: '16px',
                  backgroundColor: '#f8f9fa',
                }}>
                <h4 style={{ margin: '0 0 12px 0', color: '#007acc', fontSize: '16px' }}>Condiciones de Temperatura</h4>
                <div>
                  {formData.temperaturaNevera && (
                    <div style={{ fontSize: '13px', marginBottom: '4px' }}>
                      <strong>Nevera:</strong> {formData.temperaturaNevera}
                    </div>
                  )}
                  {formData.temperaturaAmbiente && (
                    <div style={{ fontSize: '13px' }}>
                      <strong>Ambiente:</strong> {formData.temperaturaAmbiente}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Sección de Estudios con DataGrid */}
          <div className='estudios-section' style={{ marginTop: '20px', flex: 1, display: 'flex', flexDirection: 'column' }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '16px',
                padding: '12px',
                backgroundColor: '#007acc',
                color: 'white',
                borderRadius: '8px 8px 0 0',
              }}>
              <h4 style={{ margin: 0, fontSize: '18px' }}>Estudios Seleccionados</h4>
              <div
                style={{
                  backgroundColor: 'white',
                  color: '#007acc',
                  padding: '4px 12px',
                  borderRadius: '16px',
                  fontWeight: 'bold',
                  fontSize: '14px',
                }}>
                Total: {estudiosSeleccionados.length}
              </div>
            </div>

            {/* DataGrid de Estudios */}
            <DataGrid
              dataSource={estudiosSeleccionados}
              keyExpr='Roe_Id'
              showBorders={true}
              rowAlternationEnabled={true}
              hoverStateEnabled={true}
              height={'100%'} // Altura dinámica para ocupar todo el espacio disponible
              columnAutoWidth={true}
              showColumnLines={false}
              style={{
                border: '1px solid #e0e0e0',
                borderRadius: '0 0 8px 8px',
                flex: 1,
              }}>
              <Paging enabled={true} />
              <Pager visible={true} allowedPageSizes={[20, 50, 100, 'all']} displayMode='full' showPageSizeSelector={true} showInfo={true} showNavigationButtons={true} />
              {/* Número de Orden */}
              <Column dataField='Ord_Numero' caption='ORDEN' alignment='center' width={100} cellRender={renderOrden} />

              {/* Información del Paciente */}
              <Column caption='PACIENTE' cellRender={renderPaciente} minWidth={200} />

              {/* Código del Estudio */}
              <Column dataField='Es_Codigo' caption='CÓDIGO' alignment='center' width={100} cellRender={renderEstudioCodigo} />

              {/* Nombre del Estudio */}
              <Column dataField='Es_Nombre' caption='ESTUDIO' minWidth={250} cellRender={(cellData) => <div style={{ fontSize: '13px' }}>{cellData.value}</div>} />

              {/* Área */}
              <Column
                dataField='Area_Nombre'
                caption='ÁREA'
                width={120}
                alignment='center'
                cellRender={(cellData) => (
                  <div
                    style={{
                      fontSize: '11px',
                      backgroundColor: '#f0f0f0',
                      padding: '4px 8px',
                      borderRadius: '12px',
                      textAlign: 'center',
                    }}>
                    {cellData.value}
                  </div>
                )}
              />

              {/* Cantidad */}
              <Column dataField='Rmd_Cantidad' caption='CANT.' width={80} alignment='center' cellRender={renderCantidad} />

              {/* Urgente */}
              <Column dataField='Rmd_Urgente' caption='PRIORIDAD' width={100} alignment='center' cellRender={renderUrgente} />

              {/* Notas */}
              <Column
                dataField='Rmd_Nota'
                caption='OBSERVACIONES'
                minWidth={150}
                cellRender={(cellData) => {
                  if (!cellData.value) {
                    return <span style={{ color: '#999', fontStyle: 'italic' }}>Sin observaciones</span>;
                  }

                  const truncated = cellData.value.length > 50 ? `${cellData.value.substring(0, 50)}...` : cellData.value;

                  return (
                    <div title={cellData.value} style={{ fontSize: '12px' }}>
                      {truncated}
                    </div>
                  );
                }}
              />
            </DataGrid>

            {/* Resumen de estudios */}
            <div
              style={{
                marginTop: '12px',
                padding: '12px',
                backgroundColor: '#f8f9fa',
                borderRadius: '6px',
                border: '1px solid #e0e0e0',
              }}>
              <div style={{ display: 'flex', gap: '24px', fontSize: '13px' }}>
                <div>
                  <strong>Total estudios:</strong> {estudiosSeleccionados.length}
                </div>
                <div>
                  <strong>Urgentes:</strong> {estudiosSeleccionados.filter((e) => e.Rmd_Urgente).length}
                </div>
                <div>
                  <strong>Con observaciones:</strong> {estudiosSeleccionados.filter((e) => e.Rmd_Nota && e.Rmd_Nota.trim()).length}
                </div>
              </div>
            </div>
          </div>

          {/* Notas Generales */}
          {formData.notas && (
            <div
              className='info-card'
              style={{
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                padding: '16px',
                backgroundColor: '#fff8e1',
                marginTop: '20px',
              }}>
              <h4 style={{ margin: '0 0 12px 0', color: '#f57c00', fontSize: '16px' }}>Notas Generales</h4>
              <div
                style={{
                  fontSize: '14px',
                  lineHeight: '1.5',
                  backgroundColor: 'white',
                  padding: '12px',
                  borderRadius: '4px',
                  border: '1px solid #e0e0e0',
                }}>
                {formData.notas}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const getStepTitle = () => {
    switch (currentStep) {
      case 1:
        return 'Paso 1 de 3: Información General';
      case 2:
        return 'Paso 2 de 3: Seleccionar Estudios';
      case 3:
        return 'Paso 3 de 3: Confirmar';
      default:
        return 'Nueva Remisión';
    }
  };

  return (
    <Popup visible={visible} onHiding={() => onClose(false)} dragEnabled={true} hideOnOutsideClick={false} showCloseButton={true} showTitle={true} title={getStepTitle()} wrapperAttr={{ ...wrapperAttr, class: `${wrapperAttr?.class} modal-create-remision-wrapper` }} height='98%' width={'98%'}>
      <div className='modal-create-remision'>
        {/* Progress indicator */}
        <div className='progress-steps'>
          <div className={`step ${currentStep >= 1 ? 'active' : ''} ${currentStep === 1 ? 'current' : ''}`}>
            <span>1</span>
            <div className='step-label'>Información</div>
          </div>
          <div className='step-connector'></div>
          <div className={`step ${currentStep >= 2 ? 'active' : ''} ${currentStep === 2 ? 'current' : ''}`}>
            <span>2</span>
            <div className='step-label'>Estudios</div>
          </div>
          <div className='step-connector'></div>
          <div className={`step ${currentStep >= 3 ? 'active' : ''} ${currentStep === 3 ? 'current' : ''}`}>
            <span>3</span>
            <div className='step-label'>Confirmar</div>
          </div>
        </div>

        {/* Content */}
        <div className='modal-content'>
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
        </div>
      </div>

      {/* Footer buttons via ToolbarItem */}
      <ToolbarItem toolbar='bottom' location='before'>
        <Button text='Cancelar' stylingMode='contained' onClick={() => onClose(false)} disabled={loading} className='cancel-button' />
      </ToolbarItem>
      <ToolbarItem toolbar='bottom' location='after'>
        {currentStep > 1 && <Button text='Anterior' stylingMode='contained' onClick={handlePrevious} disabled={loading} className='cancel-button' />}
        {currentStep < 3 ? <Button text='Siguiente' type='default' stylingMode='contained' onClick={handleNext} disabled={loading} /> : <Button text={loading ? 'Creando...' : 'Crear Remisión'} type='default' stylingMode='contained' onClick={handleCreate} disabled={loading} />}
      </ToolbarItem>
    </Popup>
  );
};
